import type { ExpensePurpose, ExpenseTotals } from "@bt/domain";
import { useMutation } from "@tanstack/react-query";
import {
  CircleEllipsisIcon,
  CopyPlusIcon,
  FlagIcon,
  FuelIcon,
  MoreHorizontalIcon,
  PaperclipIcon,
  ReceiptIcon,
  Trash2Icon,
  UmbrellaIcon,
  WrenchIcon,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NONE, Select } from "@/components/ui/select";
import { localToday, useFormat } from "@/lib/format";
import { type RouterOutputs, useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { draftOf, type ExpenseDraft, ExpenseSheet, type ExpenseSheetTarget } from "./expense-sheet";
import {
  CostTypeTile,
  hideUntilDeleted,
  UNDO_MS,
  useExpenseFormat,
  useHiddenExpenses,
} from "./shared";

type ExpenseList = RouterOutputs["vehicleManagement"]["expenses"]["list"];
export type ExpenseRowData = ExpenseList["months"][number]["rows"][number];

/**
 * The expense sheet and what a list does with it: open a row, add one, add one again, delete one
 * with Undo. A page renders `sheet` once and hands the actions to its rows.
 */
export function useExpenseActions() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const [target, setTarget] = useState<ExpenseSheetTarget | null>(null);
  const remove = useMutation(trpc.vehicleManagement.expenses.delete.mutationOptions());
  const del = (expense: { id: string }) => {
    const undo = hideUntilDeleted(expense.id, () => remove.mutate({ id: expense.id }));
    toast.success(t("vehicleManagement.deleted"), {
      duration: UNDO_MS,
      action: { label: t("vehicleManagement.undo"), onClick: undo },
    });
  };
  return {
    open: (id: string) => setTarget({ kind: "edit", id }),
    add: (preset?: Partial<ExpenseDraft>) => setTarget({ kind: "new", preset }),
    again: (row: ExpenseRowData) =>
      setTarget({ kind: "new", preset: { ...draftOf(row), spentOn: localToday(), mileage: null } }),
    del,
    sheet: (
      <ExpenseSheet
        target={target}
        onOpenChange={(open) => !open && setTarget(null)}
        onDelete={del}
      />
    ),
  };
}

type Actions = ReturnType<typeof useExpenseActions>;

export function ExpenseRow({
  expense,
  actions,
  showVehicle,
}: {
  expense: ExpenseRowData;
  actions: Actions;
  showVehicle: boolean;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const ef = useExpenseFormat();
  const payee = expense.supplier?.name ?? expense.payeeName;
  const meta = [
    format.shortDate(expense.spentOn),
    payee,
    showVehicle ? (expense.vehicle?.name ?? t("vehicleManagement.wholeGarage")) : null,
  ].filter(Boolean);
  const fill =
    expense.fuelQty !== null && expense.fuelUnit
      ? [
          ef.fuelQty(expense.fuelQty, expense.fuelUnit),
          expense.pricePerUnit !== null
            ? ef.pricePerUnit(expense.pricePerUnit, expense.currency, expense.fuelUnit)
            : null,
        ]
      : [];
  const mileage =
    expense.mileage !== null && expense.mileageUnit
      ? format.mileage(expense.mileage, expense.mileageUnit)
      : null;
  const extra = [...fill, mileage].filter(Boolean);
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 sm:px-4">
      <button
        type="button"
        onClick={() => actions.open(expense.id)}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      >
        <CostTypeTile costType={expense.costType} purpose={expense.purpose} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">
              {expense.comment ?? t(`vehicleManagement.costTypes.${expense.costType}`)}
            </span>
            {expense.attachmentCount ? (
              <PaperclipIcon
                className="size-3.5 shrink-0 text-muted-foreground"
                aria-label={t("vehicleManagement.receiptCount", { count: expense.attachmentCount })}
              />
            ) : null}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {expense.purpose === "event" ? (
              <Badge tone="brand" className="shrink-0">
                <FlagIcon />
                {t("vehicleManagement.eventBadge")}
              </Badge>
            ) : expense.purpose === "other" ? (
              <Badge className="shrink-0">
                <CircleEllipsisIcon />
                {t("vehicleManagement.otherBadge")}
              </Badge>
            ) : null}
            <span className="truncate">
              {expense.comment ? `${t(`vehicleManagement.costTypes.${expense.costType}`)} · ` : ""}
              {meta.join(" · ")}
            </span>
          </span>
          {extra.length ? (
            <span className="block truncate text-xs text-muted-foreground tabular">
              {extra.join(" · ")}
            </span>
          ) : null}
        </span>
        <span className="shrink-0 text-sm font-semibold tabular">
          {format.money(expense.amountMinor, expense.currency)}
        </span>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label={t("common.more")}>
            <MoreHorizontalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => actions.again(expense)}>
            <CopyPlusIcon />
            {t("vehicleManagement.addAgain")}
          </DropdownMenuItem>
          <DropdownMenuItem destructive onSelect={() => actions.del(expense)}>
            <Trash2Icon />
            {t("common.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** The list by month, each month with its sum; expenses waiting to be deleted are left out. */
export function ExpenseMonths({
  months,
  actions,
  showVehicle,
}: {
  months: ExpenseList["months"];
  actions: Actions;
  showVehicle: boolean;
}) {
  const format = useFormat();
  const hidden = useHiddenExpenses();
  return (
    <div className="flex flex-col gap-5">
      {months.map((month) => {
        const rows = month.rows.filter((r) => !hidden.has(r.id));
        if (rows.length === 0) return null;
        return (
          <section key={month.month}>
            <h3 className="mb-2 flex items-baseline justify-between px-1 text-sm">
              <span className="font-medium">{format.monthYear(`${month.month}-01`)}</span>
              <span className="text-muted-foreground tabular">
                {format.money(month.totals.totalMinor, month.totals.currency)}
              </span>
            </h3>
            <Card className="divide-y divide-border">
              {rows.map((expense) => (
                <ExpenseRow
                  key={expense.id}
                  expense={expense}
                  actions={actions}
                  showVehicle={showVehicle}
                />
              ))}
            </Card>
          </section>
        );
      })}
    </div>
  );
}

/** Total for the period, running costs and events side by side, with a slim bar of the two. */
export function TotalsCard({
  totals,
  title,
  children,
}: {
  totals: ExpenseTotals;
  title: string;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const share = (minor: number) => (totals.totalMinor > 0 ? (minor / totals.totalMinor) * 100 : 0);
  // "Other" shows only once something is in it, so most garages keep the two-way split.
  const parts: { purpose: ExpensePurpose; minor: number; dot: string }[] = [
    { purpose: "running", minor: totals.runningMinor, dot: "bg-foreground/70" },
    { purpose: "event", minor: totals.eventMinor, dot: "bg-brand" },
    ...(totals.otherMinor
      ? [{ purpose: "other" as const, minor: totals.otherMinor, dot: "bg-muted-foreground/40" }]
      : []),
  ];
  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-muted-foreground">{title}</span>
        <span className="text-2xl font-semibold tracking-tight tabular">
          {format.money(totals.totalMinor, totals.currency)}
        </span>
      </div>
      <div className="flex h-2 overflow-hidden rounded-full bg-muted" aria-hidden>
        {parts.map((p) => (
          <div key={p.purpose} className={p.dot} style={{ width: `${share(p.minor)}%` }} />
        ))}
      </div>
      <dl className={cn("grid gap-3 text-sm", parts.length === 3 ? "grid-cols-3" : "grid-cols-2")}>
        {parts.map((p) => (
          <div key={p.purpose} className="min-w-0">
            <dt className="flex items-baseline gap-1.5 text-xs text-muted-foreground">
              <span className={cn("size-2 shrink-0 translate-y-[-1px] rounded-full", p.dot)} />
              <span>{t(`vehicleManagement.purposes.${p.purpose}`)}</span>
            </dt>
            <dd className="font-medium tabular">{format.money(p.minor, totals.currency)}</dd>
          </div>
        ))}
      </dl>
      {children}
    </Card>
  );
}

/** No expenses yet: three quick ways in. */
export function ExpensesEmpty({
  onAdd,
  title,
  body,
}: {
  onAdd: (preset: Partial<ExpenseDraft>) => void;
  title: string;
  body: string;
}) {
  const { t } = useTranslation();
  return (
    <EmptyState
      compact
      icon={ReceiptIcon}
      title={title}
      body={body}
      action={(
        [
          ["fuel", FuelIcon],
          ["workshop", WrenchIcon],
          ["insurance", UmbrellaIcon],
        ] as const
      ).map(([costType, Icon]) => (
        <Button key={costType} variant="outline" onClick={() => onAdd({ costType })}>
          <Icon />
          {t(`vehicleManagement.costTypes.${costType}`)}
        </Button>
      ))}
    />
  );
}

/** A year picker for the totals card and the list. */
export function YearSelect({
  years,
  value,
  onChange,
  className,
}: {
  years: number[];
  value: number | null;
  onChange: (year: number | null) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <Select
      size="sm"
      aria-label={t("vehicleManagement.year")}
      className={cn("w-32", className)}
      value={value === null ? NONE : String(value)}
      onValueChange={(v) => onChange(v === NONE ? null : Number(v))}
      options={[
        { value: NONE, label: t("vehicleManagement.allYears") },
        ...[...new Set([...(value === null ? [] : [value]), ...years])]
          .sort((a, b) => b - a)
          .map((year) => ({ value: String(year), label: String(year) })),
      ]}
    />
  );
}
